import React, { useEffect, useState, useCallback } from 'react';
import {
  Line, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, ResponsiveContainer, Legend, CartesianGrid, Area, AreaChart
} from 'recharts';
import api from '../api/axios';
import { useToast } from '../context/ToastContext';

const EXPENSE_CATS = [
  { value: 'purchase', label: 'Product Purchase', icon: '🛒' },
  { value: 'fuel', label: 'Petrol / Diesel', icon: '⛽' },
  { value: 'rent', label: 'Rent', icon: '🏠' },
  { value: 'utilities', label: 'Utilities', icon: '⚡' },
  { value: 'transport', label: 'Transport', icon: '🚚' },
  { value: 'vehicle_maintenance', label: 'Vehicle Maintenance', icon: '🔧' },
  { value: 'maintenance', label: 'Shop Maintenance', icon: '🏗️' },
  { value: 'packaging', label: 'Packaging', icon: '📦' },
  { value: 'loading', label: 'Loading / Unloading', icon: '🏋️' },
  { value: 'supplies', label: 'Supplies', icon: '🧹' },
  { value: 'marketing', label: 'Marketing', icon: '📣' },
  { value: 'insurance', label: 'Insurance', icon: '🛡️' },
  { value: 'salary_advance', label: 'Salary Advance', icon: '💵' },
  { value: 'other', label: 'Other', icon: '📋' },
];

const PAYMENT_METHODS = [
  { value: 'cash', label: 'Cash' },
  { value: 'bank_transfer', label: 'Bank Transfer' },
  { value: 'upi', label: 'UPI' },
  { value: 'cheque', label: 'Cheque' },
  { value: 'other', label: 'Other' },
];

const PIE_COLORS = ['#16a34a', '#facc15', '#f59e0b', '#ef4444', '#22c55e', '#8b5cf6', '#ec4899', '#f97316', '#14b8a6', '#84cc16', '#e11d48', '#0ea5e9'];

const FinancePage = () => {
  const [tab, setTab] = useState('pnl');
  const [pnlData, setPnlData] = useState(null);
  const [trendData, setTrendData] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [year, setYear] = useState(new Date().getFullYear());
  const [expMonth, setExpMonth] = useState(new Date().getMonth() + 1);
  const [expYear, setExpYear] = useState(new Date().getFullYear());
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ title: '', amount: '', category: 'other', expenseDate: new Date().toISOString().split('T')[0], description: '', paymentMethod: 'cash' });
  const toast = useToast();

  const monthNames = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

  const formatCurrency = (v) => `₹${parseFloat(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;

  const fetchPnl = useCallback(async () => {
    try {
      const [pnlRes, trendRes] = await Promise.all([
        api.get(`/finance/pnl?month=${month}&year=${year}`),
        api.get('/finance/pnl/trend?months=6'),
      ]);
      setPnlData(pnlRes.data.data);
      setTrendData(trendRes.data.data);
    } catch { toast('Failed to load P&L data', 'error'); }
    finally { setLoading(false); }
  }, [month, year, toast]);

  const fetchExpenses = useCallback(async () => {
    try {
      const { data } = await api.get(`/finance/expenses?month=${expMonth}&year=${expYear}`);
      setExpenses(data.data);
    } catch { toast('Failed to load expenses', 'error'); }
    finally { setLoading(false); }
  }, [expMonth, expYear, toast]);

  useEffect(() => {
    setLoading(true);
    if (tab === 'pnl') fetchPnl();
    else fetchExpenses();
  }, [tab, fetchPnl, fetchExpenses]);

  const openAddExpense = () => {
    setEditing(null);
    setForm({ title: '', amount: '', category: 'other', expenseDate: new Date().toISOString().split('T')[0], description: '', paymentMethod: 'cash' });
    setShowModal(true);
  };

  const openEditExpense = (e) => {
    setEditing(e);
    setForm({
      title: e.title, amount: e.amount, category: e.category,
      expenseDate: e.expense_date?.split('T')[0] || '',
      description: e.description || '', paymentMethod: e.payment_method || 'cash'
    });
    setShowModal(true);
  };

  const handleSaveExpense = async (ev) => {
    ev.preventDefault();
    if (!form.title || !form.amount || !form.category) {
      toast('Title, amount, and category are required', 'error');
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/finance/expenses/${editing.id}`, form);
        toast('Expense updated!', 'success');
      } else {
        await api.post('/finance/expenses', form);
        toast('Expense added!', 'success');
      }
      setShowModal(false);
      fetchExpenses();
    } catch (err) {
      toast(err.response?.data?.message || 'Failed to save expense', 'error');
    } finally { setSaving(false); }
  };

  const handleDeleteExpense = async (id) => {
    if (!window.confirm('Delete this expense?')) return;
    try {
      await api.delete(`/finance/expenses/${id}`);
      toast('Expense deleted', 'success');
      fetchExpenses();
    } catch { toast('Failed to delete', 'error'); }
  };

  if (loading) return <div className="flex-center" style={{ height: 300 }}><div className="spinner"></div></div>;

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h2 style={{ fontSize: 22, fontWeight: 700 }}>📊 Finance & P&L</h2>
          <p style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: 4 }}>Track revenue, expenses, wastage, and profit/loss</p>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, marginBottom: 24, background: 'var(--dark-2)', borderRadius: 'var(--radius-sm)', padding: 4, border: '1px solid var(--border)', width: 'fit-content' }}>
        {[{ key: 'pnl', label: '📈 Profit & Loss' }, { key: 'expenses', label: '💸 Daily Expenses' }].map(t => (
          <button key={t.key} onClick={() => setTab(t.key)}
            style={{
              padding: '8px 18px', borderRadius: 6, border: 'none', cursor: 'pointer',
              fontSize: 13, fontWeight: 600, fontFamily: 'inherit',
              background: tab === t.key ? 'linear-gradient(135deg, var(--primary), var(--primary-dark))' : 'transparent',
              color: tab === t.key ? '#fff' : 'var(--text-muted)',
              transition: 'all 0.2s'
            }}
          >{t.label}</button>
        ))}
      </div>

      {/* P&L TAB */}
      {tab === 'pnl' && pnlData && (
        <div>
          {/* Date filter */}
          <div style={{ display: 'flex', gap: 12, marginBottom: 20, alignItems: 'center' }}>
            <select className="form-select" style={{ width: 160 }} value={month} onChange={e => setMonth(parseInt(e.target.value))}>
              {monthNames.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
            </select>
            <select className="form-select" style={{ width: 100 }} value={year} onChange={e => setYear(parseInt(e.target.value))}>
              {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>

          {/* P&L Summary Cards */}
          <div className="stats-grid" style={{ marginBottom: 24, gridTemplateColumns: 'repeat(5, 1fr)' }}>
            <div className="stat-card green">
              <div className="stat-icon">💰</div>
              <div className="stat-value">{formatCurrency(pnlData.revenue.total)}</div>
              <div className="stat-label">Total Sales</div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>
                Orders: {formatCurrency(pnlData.revenue.orders)} | Counter: {formatCurrency(pnlData.revenue.counterSales)}
              </div>
            </div>
            <div className="stat-card red">
              <div className="stat-icon">📉</div>
              <div className="stat-value">{formatCurrency(pnlData.expenses.total)}</div>
              <div className="stat-label">Total Expenses</div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>
                Salary: {formatCurrency(pnlData.expenses.salary)} | Operating: {formatCurrency(pnlData.expenses.totalOther)}
              </div>
            </div>
            <div className="stat-card" style={{ borderLeft: '4px solid #f43f5e' }}>
              <div className="stat-icon">🗑️</div>
              <div className="stat-value" style={{ color: '#f43f5e' }}>{formatCurrency(pnlData.wastage?.totalCost || 0)}</div>
              <div className="stat-label">Wastage Loss</div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>
                {pnlData.wastage?.totalItems || 0} items wasted
              </div>
            </div>
            <div className={`stat-card ${pnlData.netProfit >= 0 ? 'green' : 'red'}`}>
              <div className="stat-icon">{pnlData.netProfit >= 0 ? '📈' : '📉'}</div>
              <div className="stat-value" style={{ color: pnlData.netProfit >= 0 ? 'var(--success)' : 'var(--danger)' }}>
                {pnlData.netProfit >= 0 ? '' : '-'}{formatCurrency(Math.abs(pnlData.netProfit))}
              </div>
              <div className="stat-label">Net {pnlData.netProfit >= 0 ? 'Profit' : 'Loss'}</div>
            </div>
            <div className="stat-card blue">
              <div className="stat-icon">📊</div>
              <div className="stat-value">{pnlData.profitMargin}%</div>
              <div className="stat-label">Profit Margin</div>
            </div>
          </div>

          {/* Charts Row */}
          <div className="charts-grid">
            {/* P&L Trend Chart */}
            <div className="card">
              <div className="card-header">
                <span className="card-title">📈 Revenue vs Expenses vs Wastage — Last 6 Months</span>
              </div>
              <ResponsiveContainer width="100%" height={280}>
                <AreaChart data={trendData}>
                  <defs>
                    <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#10b981" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#10b981" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="expGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#ef4444" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="wastGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#f43f5e" stopOpacity={0.3} />
                      <stop offset="95%" stopColor="#f43f5e" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                  <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#94a3b8' }} />
                  <YAxis tick={{ fontSize: 11, fill: '#94a3b8' }} tickFormatter={v => `₹${(v / 1000).toFixed(0)}k`} />
                  <Tooltip
                    formatter={(v, name) => [formatCurrency(v), name]}
                    contentStyle={{ background: '#1e293b', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8, color: '#f1f5f9' }}
                  />
                  <Legend />
                  <Area type="monotone" dataKey="revenue" name="Revenue" stroke="#16a34a" fill="url(#revGrad)" strokeWidth={2.5} />
                  <Area type="monotone" dataKey="expenses" name="Expenses" stroke="#ef4444" fill="url(#expGrad)" strokeWidth={2.5} />
                  <Area type="monotone" dataKey="wastage" name="Wastage" stroke="#f43f5e" fill="url(#wastGrad)" strokeWidth={2} strokeDasharray="5 5" />
                  <Line type="monotone" dataKey="profit" name="Profit" stroke="#facc15" strokeWidth={2.5} dot={{ fill: '#facc15', r: 4 }} />
                </AreaChart>
              </ResponsiveContainer>
            </div>

            {/* Expense Breakdown Pie */}
            <div className="card">
              <div className="card-header">
                <span className="card-title">🍩 Expense Breakdown</span>
              </div>
              {(pnlData.expenses.others.length > 0 || pnlData.expenses.salary > 0 || (pnlData.wastage?.totalCost || 0) > 0) ? (
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart>
                    <Pie
                      data={[
                        ...pnlData.expenses.others,
                        ...(pnlData.expenses.salary > 0 ? [{ category: 'salary', amount: pnlData.expenses.salary }] : []),
                        ...((pnlData.wastage?.totalCost || 0) > 0 ? [{ category: 'wastage', amount: pnlData.wastage.totalCost }] : []),
                      ]}
                      dataKey="amount"
                      nameKey="category"
                      cx="50%"
                      cy="50%"
                      outerRadius={90}
                      label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                      labelLine={false}
                      fontSize={10}
                    >
                      {[
                        ...pnlData.expenses.others,
                        ...(pnlData.expenses.salary > 0 ? [{ category: 'salary' }] : []),
                        ...((pnlData.wastage?.totalCost || 0) > 0 ? [{ category: 'wastage' }] : []),
                      ].map((e, i) => (
                        <Cell key={i} fill={e.category === 'wastage' ? '#f43f5e' : PIE_COLORS[i % PIE_COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip
                      formatter={(v) => [formatCurrency(v), 'Amount']}
                      contentStyle={{ background: '#1e293b', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 8, color: '#f1f5f9' }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
                  <div style={{ fontSize: 32, marginBottom: 8 }}>📊</div>
                  <p style={{ fontSize: 13 }}>No expenses recorded</p>
                </div>
              )}
            </div>
          </div>

          {/* Wastage Breakdown Table */}
          {pnlData.wastage && pnlData.wastage.breakdown && pnlData.wastage.breakdown.length > 0 && (
            <div className="card" style={{ marginBottom: 24 }}>
              <div className="card-header">
                <span className="card-title">🗑️ Wastage Breakdown — Top Wasted Products</span>
                <span style={{ fontSize: 12, color: '#f43f5e', fontWeight: 700 }}>
                  Total: {formatCurrency(pnlData.wastage.totalCost)} ({pnlData.wastage.totalItems} items)
                </span>
              </div>
              <div className="table-wrapper">
                <table>
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Product</th>
                      <th>Qty Wasted</th>
                      <th>Unit Price</th>
                      <th>Total Loss</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pnlData.wastage.breakdown.map((w, i) => (
                      <tr key={i}>
                        <td style={{ fontWeight: 600, color: 'var(--text-muted)' }}>{i + 1}</td>
                        <td style={{ fontWeight: 600 }}>{w.product_name}</td>
                        <td>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '2px 10px', borderRadius: 12, background: 'rgba(244,63,94,0.15)', color: '#f43f5e', fontWeight: 700, fontSize: 13 }}>
                            {w.total_qty}
                          </span>
                        </td>
                        <td style={{ color: 'var(--text-muted)' }}>{formatCurrency(w.unit_price)}</td>
                        <td style={{ fontWeight: 800, color: '#f43f5e' }}>{formatCurrency(w.cost)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* P&L Statement Details */}
          <div className="card">
            <div className="card-header">
              <span className="card-title">📑 P&L Statement — {monthNames[month - 1]} {year}</span>
            </div>
            <div className="resp-grid-2">
              {/* Revenue side */}
              <div>
                <h4 style={{ fontSize: 14, fontWeight: 700, color: 'var(--success)', marginBottom: 16 }}>💰 REVENUE (Sales)</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                    <span style={{ fontSize: 13 }}>📦 Online Order Revenue</span>
                    <span style={{ fontWeight: 700 }}>{formatCurrency(pnlData.revenue.orders)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                    <span style={{ fontSize: 13 }}>🏪 Counter Sales Revenue</span>
                    <span style={{ fontWeight: 700 }}>{formatCurrency(pnlData.revenue.counterSales)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', background: 'rgba(16,185,129,0.1)', borderRadius: 8, padding: '10px 12px' }}>
                    <span style={{ fontWeight: 800, fontSize: 14 }}>Total Sales Revenue</span>
                    <span style={{ fontWeight: 800, fontSize: 16, color: 'var(--success)' }}>{formatCurrency(pnlData.revenue.total)}</span>
                  </div>
                </div>
              </div>

              {/* Expense side */}
              <div>
                <h4 style={{ fontSize: 14, fontWeight: 700, color: 'var(--danger)', marginBottom: 16 }}>📉 EXPENSES & LOSSES</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                    <span style={{ fontSize: 13 }}>💼 Salary / Payroll</span>
                    <span style={{ fontWeight: 700 }}>{formatCurrency(pnlData.expenses.salary)}</span>
                  </div>
                  {pnlData.expenses.others.map((e, i) => (
                    <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border)' }}>
                      <span style={{ fontSize: 13 }}>{EXPENSE_CATS.find(c => c.value === e.category)?.icon || '📋'} {EXPENSE_CATS.find(c => c.value === e.category)?.label || e.category}</span>
                      <span style={{ fontWeight: 700 }}>{formatCurrency(e.amount)}</span>
                    </div>
                  ))}
                  {/* Wastage line item */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px solid var(--border)', background: 'rgba(244,63,94,0.05)', borderRadius: 6, paddingLeft: 8, paddingRight: 8 }}>
                    <span style={{ fontSize: 13, color: '#f43f5e' }}>🗑️ Wastage Loss ({pnlData.wastage?.totalItems || 0} items)</span>
                    <span style={{ fontWeight: 700, color: '#f43f5e' }}>{formatCurrency(pnlData.wastage?.totalCost || 0)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', background: 'rgba(239,68,68,0.1)', borderRadius: 8, padding: '10px 12px' }}>
                    <span style={{ fontWeight: 800, fontSize: 14 }}>Total Expenses + Wastage</span>
                    <span style={{ fontWeight: 800, fontSize: 16, color: 'var(--danger)' }}>{formatCurrency(pnlData.expenses.total)}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Net result */}
            <div style={{ marginTop: 20, padding: 16, background: pnlData.netProfit >= 0 ? 'linear-gradient(135deg, rgba(16,185,129,0.15), rgba(6,182,212,0.1))' : 'linear-gradient(135deg, rgba(239,68,68,0.15), rgba(245,158,11,0.1))', borderRadius: 'var(--radius-sm)', textAlign: 'center' }}>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 }}>NET {pnlData.netProfit >= 0 ? 'PROFIT' : 'LOSS'}</div>
              <div style={{ fontSize: 10, color: 'var(--text-muted)', marginBottom: 8 }}>
                (Total Sales − Expenses − Wastage)
              </div>
              <div style={{ fontSize: 32, fontWeight: 900, color: pnlData.netProfit >= 0 ? 'var(--success)' : 'var(--danger)' }}>
                {pnlData.netProfit >= 0 ? '+' : '-'}{formatCurrency(Math.abs(pnlData.netProfit))}
              </div>
              <div style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4 }}>
                Profit Margin: {pnlData.profitMargin}%
              </div>
            </div>
          </div>
        </div>
      )}

      {/* EXPENSES TAB */}
      {tab === 'expenses' && (
        <div>
          <div style={{ display: 'flex', gap: 12, marginBottom: 20, alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <select className="form-select" style={{ width: 160 }} value={expMonth} onChange={e => setExpMonth(parseInt(e.target.value))}>
                {monthNames.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
              </select>
              <select className="form-select" style={{ width: 100 }} value={expYear} onChange={e => setExpYear(parseInt(e.target.value))}>
                {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>
            <button className="btn btn-primary" onClick={openAddExpense}>+ Add Daily Expense</button>
          </div>

          {/* Quick Add Buttons */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
            {[
              { cat: 'purchase', title: 'Product Purchase', icon: '🛒' },
              { cat: 'fuel', title: 'Petrol / Diesel', icon: '⛽' },
              { cat: 'transport', title: 'Transport', icon: '🚚' },
              { cat: 'vehicle_maintenance', title: 'Vehicle Repair', icon: '🔧' },
              { cat: 'packaging', title: 'Packaging', icon: '📦' },
              { cat: 'loading', title: 'Loading', icon: '🏋️' },
            ].map(q => (
              <button
                key={q.cat}
                onClick={() => {
                  setEditing(null);
                  setForm({ title: q.title, amount: '', category: q.cat, expenseDate: new Date().toISOString().split('T')[0], description: '', paymentMethod: 'cash' });
                  setShowModal(true);
                }}
                style={{
                  padding: '8px 14px', borderRadius: 8, border: '1px solid var(--border)',
                  background: 'var(--dark-2)', color: 'var(--text)', cursor: 'pointer',
                  fontSize: 12, fontWeight: 600, fontFamily: 'inherit',
                  display: 'flex', alignItems: 'center', gap: 6,
                  transition: 'all 0.2s',
                }}
                onMouseOver={e => { e.currentTarget.style.background = 'var(--primary)'; e.currentTarget.style.color = '#fff'; e.currentTarget.style.borderColor = 'var(--primary)'; }}
                onMouseOut={e => { e.currentTarget.style.background = 'var(--dark-2)'; e.currentTarget.style.color = 'var(--text)'; e.currentTarget.style.borderColor = 'var(--border)'; }}
              >
                <span>{q.icon}</span> {q.title}
              </button>
            ))}
          </div>

          {/* Expense Stats */}
          {expenses.length > 0 && (
            <div className="stats-grid" style={{ marginBottom: 20 }}>
              <div className="stat-card red">
                <div className="stat-icon">💸</div>
                <div className="stat-value">{formatCurrency(expenses.reduce((s, e) => s + parseFloat(e.amount), 0))}</div>
                <div className="stat-label">Total Expenses</div>
              </div>
              <div className="stat-card blue">
                <div className="stat-icon">📋</div>
                <div className="stat-value">{expenses.length}</div>
                <div className="stat-label">Entries</div>
              </div>
              <div className="stat-card yellow">
                <div className="stat-icon">💰</div>
                <div className="stat-value">{formatCurrency(expenses.reduce((s, e) => s + parseFloat(e.amount), 0) / (expenses.length || 1))}</div>
                <div className="stat-label">Avg per Entry</div>
              </div>
              {/* Category breakdown mini stat */}
              <div className="stat-card" style={{ borderLeft: '4px solid #8b5cf6' }}>
                <div className="stat-icon">📊</div>
                <div className="stat-value" style={{ fontSize: 16 }}>
                  {[...new Set(expenses.map(e => e.category))].length}
                </div>
                <div className="stat-label">Categories</div>
              </div>
            </div>
          )}

          {expenses.length === 0 ? (
            <div className="empty-state"><div className="empty-icon">💸</div><p>No expenses for {monthNames[expMonth - 1]} {expYear}</p>
              <button className="btn btn-primary" onClick={openAddExpense}>+ Add Daily Expense</button></div>
          ) : (
            <div className="card">
              <div className="table-wrapper">
                <table>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Title</th>
                      <th>Category</th>
                      <th>Amount</th>
                      <th>Payment</th>
                      <th>By</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {expenses.map(e => (
                      <tr key={e.id}>
                        <td style={{ fontWeight: 600, fontSize: 12 }}>
                          {e.expense_date ? new Date(e.expense_date).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—'}
                        </td>
                        <td>
                          <div style={{ fontWeight: 600 }}>{e.title}</div>
                          {e.description && <div style={{ fontSize: 11, color: 'var(--text-muted)', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.description}</div>}
                        </td>
                        <td>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 600, padding: '2px 8px', borderRadius: 8, background: 'rgba(99,102,241,0.1)' }}>
                            {EXPENSE_CATS.find(c => c.value === e.category)?.icon} {EXPENSE_CATS.find(c => c.value === e.category)?.label || e.category}
                          </span>
                        </td>
                        <td style={{ fontWeight: 800, fontSize: 15, color: 'var(--danger)' }}>{formatCurrency(e.amount)}</td>
                        <td style={{ fontSize: 12 }}>{e.payment_method || '—'}</td>
                        <td style={{ fontSize: 12, color: 'var(--text-muted)' }}>{e.created_by || '—'}</td>
                        <td>
                          <div style={{ display: 'flex', gap: 6 }}>
                            <button className="btn btn-secondary btn-sm" onClick={() => openEditExpense(e)}>✏️</button>
                            <button className="btn btn-danger btn-sm" onClick={() => handleDeleteExpense(e.id)}>🗑</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Add/Edit Expense Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={ev => ev.stopPropagation()} style={{ maxWidth: 580 }}>
            <div className="modal-header">
              <h3 className="modal-title">{editing ? 'Edit Expense' : '💸 Add Daily Expense'}</h3>
              <button className="modal-close" onClick={() => setShowModal(false)}>✕</button>
            </div>
            <form onSubmit={handleSaveExpense} className="form-grid">
              <div className="form-grid form-grid-2">
                <div className="form-group">
                  <label className="form-label">Title *</label>
                  <input className="form-input" placeholder="e.g. Morning vegetables purchase" value={form.title} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Amount (₹) *</label>
                  <input className="form-input" type="number" placeholder="0" value={form.amount} onChange={e => setForm(p => ({ ...p, amount: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Category *</label>
                  <select className="form-select" value={form.category} onChange={e => setForm(p => ({ ...p, category: e.target.value }))}>
                    {EXPENSE_CATS.map(c => <option key={c.value} value={c.value}>{c.icon} {c.label}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Date</label>
                  <input className="form-input" type="date" value={form.expenseDate} onChange={e => setForm(p => ({ ...p, expenseDate: e.target.value }))} />
                </div>
                <div className="form-group">
                  <label className="form-label">Payment Method</label>
                  <select className="form-select" value={form.paymentMethod} onChange={e => setForm(p => ({ ...p, paymentMethod: e.target.value }))}>
                    {PAYMENT_METHODS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Description / Notes</label>
                <textarea className="form-textarea" placeholder="Optional notes — e.g. Petrol for delivery van, supplier bill reference..." value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} />
              </div>
              <div className="modal-footer" style={{ margin: 0, padding: 0, border: 'none', marginTop: 16 }}>
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>{saving ? 'Saving...' : editing ? 'Update' : 'Add Expense'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default FinancePage;
